/**
 * Reusable content primitives for category-authority pages.
 * Kept minimal — each is a small styled block with a data-testid.
 */
import { Link } from "react-router-dom";

/**
 * Answer-first callout — used at the top of each H2 question section.
 * Search engines and AI assistants can extract the short answer directly.
 */
export function AnswerBlock({ children }) {
  return (
    <div
      className="border-l-2 border-brand pl-5 py-1 bg-surface-subtle/40 my-4"
      data-testid="answer-block"
    >
      <p className="text-[15px] leading-relaxed text-ink">
        <strong className="text-ink font-semibold">Answer.</strong>{" "}
        {children}
      </p>
    </div>
  );
}

/**
 * Two-column comparison table primitive.
 *   headers = [leftLabel, rightLabel]
 *   rows    = [{ q: "Row question", a: "left value", b: "right value" }]
 */
export function CompareTable({ headers, rows, leftHint, rightHint }) {
  return (
    <div
      className="mt-6 overflow-x-auto border border-line"
      data-testid="compare-table"
    >
      <table className="w-full text-sm">
        <thead className="bg-surface-subtle">
          <tr>
            <th className="text-left px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-ink-subtle w-1/3">
              &nbsp;
            </th>
            <th className="text-left px-5 py-3 font-heading text-sm font-semibold text-ink border-l border-line">
              {headers[0]}
              {leftHint && (
                <div className="mt-1 font-normal text-xs text-ink-muted font-sans">
                  {leftHint}
                </div>
              )}
            </th>
            <th className="text-left px-5 py-3 font-heading text-sm font-semibold text-ink border-l border-line">
              {headers[1]}
              {rightHint && (
                <div className="mt-1 font-normal text-xs text-ink-muted font-sans">
                  {rightHint}
                </div>
              )}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-surface-subtle/40"}>
              <td className="px-5 py-4 text-ink font-medium align-top">{r.q}</td>
              <td className="px-5 py-4 text-ink-muted border-l border-line align-top">
                {r.a}
              </td>
              <td className="px-5 py-4 text-ink-muted border-l border-line align-top">
                {r.b}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Three-column comparison table primitive (for GPS vs BLE vs Cellular).
 */
export function TriCompareTable({ headers, rows }) {
  return (
    <div
      className="mt-6 overflow-x-auto border border-line"
      data-testid="tri-compare-table"
    >
      <table className="w-full text-sm">
        <thead className="bg-surface-subtle">
          <tr>
            <th className="text-left px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-ink-subtle w-1/4">
              &nbsp;
            </th>
            {headers.map((h) => (
              <th
                key={h}
                className="text-left px-5 py-3 font-heading text-sm font-semibold text-ink border-l border-line"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-surface-subtle/40"}>
              <td className="px-5 py-4 text-ink font-medium align-top">{r.q}</td>
              {r.values.map((v, j) => (
                <td
                  key={j}
                  className="px-5 py-4 text-ink-muted border-l border-line align-top"
                >
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Numbered process step used in "How disposable trackers work" style flows.
 */
export function ProcessStep({ n, title, children }) {
  return (
    <div className="border border-line bg-white p-6 lg:p-7">
      <div className="flex items-baseline gap-4">
        <span className="font-mono text-xs text-brand tracking-wider">
          {String(n).padStart(2, "0")}
        </span>
        <h3 className="font-heading text-lg font-semibold text-ink">
          {title}
        </h3>
      </div>
      <div className="mt-3 text-[15px] leading-relaxed text-ink-muted">
        {children}
      </div>
    </div>
  );
}

/**
 * Specification table for verified product specs.
 * Renders as label / value rows so each spec is individually crawlable.
 */
export function SpecTable({ title, rows, note }) {
  return (
    <div
      className="mt-6 border border-line"
      data-testid="spec-table"
    >
      <div className="px-5 py-3 border-b border-line bg-surface-subtle">
        <h4 className="font-heading text-sm font-semibold text-ink">
          {title}
        </h4>
      </div>
      <dl className="divide-y divide-line">
        {rows.map((r) => (
          <div
            key={r.k}
            className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-2 px-5 py-3"
          >
            <dt className="font-mono text-[11px] uppercase tracking-widest text-ink-subtle self-start">
              {r.k}
            </dt>
            <dd className="text-sm text-ink">{r.v}</dd>
          </div>
        ))}
      </dl>
      {note && (
        <div className="px-5 py-3 border-t border-line text-xs text-ink-muted italic">
          {note}
        </div>
      )}
    </div>
  );
}

/**
 * "Last reviewed" footer stamp for technical guides.
 */
export function ReviewStamp({ date, basis }) {
  return (
    <div
      className="mt-16 pt-6 border-t border-line text-xs text-ink-muted"
      data-testid="review-stamp"
    >
      <p>
        <span className="font-mono uppercase tracking-widest text-ink-subtle mr-3">
          Last reviewed
        </span>
        {date}
      </p>
      {basis && (
        <p className="mt-1">
          <span className="font-mono uppercase tracking-widest text-ink-subtle mr-3">
            Technical basis
          </span>
          {basis}
        </p>
      )}
    </div>
  );
}

/**
 * Compact section wrapper — consistent vertical rhythm + max-width prose.
 */
export function Prose({ children, className = "" }) {
  return (
    <div className={`prose-block max-w-3xl ${className}`}>{children}</div>
  );
}

/**
 * Breadcrumb — visible + emits BreadcrumbList schema via useJsonLd elsewhere.
 */
export function Breadcrumb({ trail }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="text-xs text-ink-muted font-mono mb-8"
      data-testid="breadcrumb"
    >
      <ol className="flex flex-wrap items-center gap-2">
        {trail.map((t, i) => (
          <li key={t.href || t.label} className="flex items-center gap-2">
            {i > 0 && <span className="text-ink-subtle">/</span>}
            {t.href ? (
              <Link
                to={t.href}
                className="hover:text-brand transition-colors"
              >
                {t.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-ink">
                {t.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
